import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { PlayCircle, X } from 'lucide-react';
import { TUTORIAL_VIDEOS } from '../pages/AppTutorials';

const STORAGE_KEY_PREFIX = 'wfmc_intro_seen_v1_';

/**
 * Auto-opens once for a signed-in user (keyed per-user in localStorage).
 * Plays the Intro Movie and shows quick-jump links to the other tutorial videos.
 * The user can dismiss with the X or "Got it" button; both mark it as seen.
 */
const IntroVideoPopup = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const storageKey = user ? `${STORAGE_KEY_PREFIX}${user.id || user.email}` : null;

  useEffect(() => {
    if (!user || !storageKey) return;
    try {
      const seen = localStorage.getItem(storageKey);
      if (!seen) setOpen(true);
    } catch {
      // localStorage disabled — just skip; user can still open from Dashboard button.
    }
  }, [user, storageKey]);

  const markSeen = () => {
    try {
      if (storageKey) localStorage.setItem(storageKey, '1');
    } catch { /* ignore */ }
    setOpen(false);
  };

  if (!user) return null;

  const intro = TUTORIAL_VIDEOS[0];
  const others = TUTORIAL_VIDEOS.slice(1);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) markSeen(); }}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto p-0"
        data-testid="intro-video-popup"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
            <PlayCircle className="w-6 h-6 text-teal-600" />
            Welcome to the Discipleship Journey App
          </DialogTitle>
          <DialogDescription>
            Take 4 minutes to see how everything fits together. You can revisit this any time from the Dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-5">
          {/* Featured intro video */}
          <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
            <iframe
              src={`https://www.youtube.com/embed/${intro.id}?autoplay=0`}
              title={intro.title}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="intro-video-iframe"
            />
          </div>

          {/* "Watched it — don't show again" quick action right under the video */}
          <label
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 cursor-pointer select-none"
            data-testid="intro-dont-show-again-row"
          >
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
              onChange={(e) => {
                if (e.target.checked) markSeen();
              }}
              data-testid="intro-dont-show-again-checkbox"
            />
            <span>Watched it — don&apos;t show this again</span>
          </label>

          {/* Quick-jump links to other tutorials */}
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2">
              Want a walk-through of a specific area?
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {others.map((v) => (
                <button
                  key={v.id}
                  onClick={() => {
                    markSeen();
                    navigate(`/tutorials#${v.id}`);
                  }}
                  className="flex items-center gap-2 p-2 text-left rounded-lg border border-slate-200 hover:border-teal-400 hover:bg-teal-50/40 transition-colors"
                  data-testid={`intro-quick-link-${v.id}`}
                >
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${v.accent}`}>
                    <PlayCircle className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{v.title}</p>
                    <p className="text-xs text-slate-400">{v.duration}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <Button
              variant="ghost"
              onClick={markSeen}
              className="text-slate-500 hover:text-slate-700"
              data-testid="intro-skip-btn"
            >
              <X className="w-4 h-4 mr-1" /> Maybe later
            </Button>
            <Button
              onClick={() => {
                markSeen();
                navigate('/tutorials');
              }}
              className="bg-[#2B6B7F] hover:bg-[#245565]"
              data-testid="intro-view-all-btn"
            >
              View all tutorials
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IntroVideoPopup;
